import { Button } from '@casavo/base-ui'
import { useFormikContext } from 'formik'
import { motion } from 'framer-motion'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import React, { useEffect, useState } from 'react'

import CasavoAssistantPhone from '@/components/ContactAgencyForm/CasavoAssistantPhone'
import Disclaimer from '@/components/Disclaimer'
import { FormikCheckbox } from '@/components/FormFields/FormikCheckbox'
import FormikInput from '@/components/FormFields/FormikInput'
import FormikSelect from '@/components/FormFields/FormikSelect'
import ReadMoreToggle from '@/components/ReadMoreToggle'
import { useDevice } from '@/context/Device'
import { Divider } from '@/design-lib/components/Divider'
import { Toast } from '@/design-lib/components/Toast'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { marginBottom } from '@/shared-styles/styles.css'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { handleFormFieldSet } from '@/utils/tracking/eventsHandlers'
import { isValidForTracking } from '@/utils/tracking/trackingUtils'

import { DisclaimerRecaptcha } from '../DisclaimerRecaptcha'

import {
  disclaimer,
  errorToast,
  formWrapper,
  gdprMotionDiv,
  gridContainer,
  inputWrapper,
  recaptchaDisclaimer,
} from './styles.css'
import { FormFields } from './types'

type ContentProps = {
  hasError: boolean
  loading: boolean
  phoneNumber: string
  property: ListingCate
  sendingMail: boolean
}

/** This component could be dead code */
const FormContentCasavo: React.FC<ContentProps> = ({ hasError, loading, phoneNumber, property, sendingMail }) => {
  const { errors, handleSubmit, values } = useFormikContext<FormFields>()
  const [showGdpr, setShowGdpr] = useState(false)
  const device = useDevice()
  const { lang, t } = useTranslation()

  const submitLabel = t('contact-form:submit')

  const isEmailValid = !errors.leadEmail && values.leadEmail !== ''
  const isPhoneNumberValid = !errors.leadPhoneNumber && values.leadPhoneNumber !== ''
  const isReasonValid = !errors.leadReason && values.leadReason !== ''

  const isButtonDisabled = sendingMail || !isEmailValid || !isPhoneNumberValid || !isReasonValid

  const onFormUpdate = (fieldName: string, value) => {
    if (isValidForTracking(value)) {
      handleFormFieldSet('ListingDetailForm', fieldName)
    }
  }

  useEffect(() => {
    setShowGdpr(Boolean(values.leadEmail) && Boolean(values.leadPhoneNumber) && Boolean(values.leadReason))
  }, [values.leadEmail, values.leadPhoneNumber, values.leadReason])

  const selectReasonOptions = [
    {
      label: t('contact-form:casavo.reason.options.morePhotos'),
      value: 'MORE_PHOTOS',
    },
    {
      label: t('contact-form:casavo.reason.options.moreAboutHouse'),
      value: 'MORE_ABOUT_HOUSE',
    },
    {
      label: t('contact-form:casavo.reason.options.other'),
      value: 'OTHER',
    },
  ]

  return (
    <>
      <form className={formWrapper} onSubmit={handleSubmit}>
        <div
          className={sprinkles({
            position: { mobile: 'static', tablet: 'relative' },
            zIndex: 2,
          })}
        >
          <div className={inputWrapper}>
            <FormikSelect
              dataTestId="contact-reason-select"
              error={t('contact-form:casavo.reason.warning')}
              handleTracking={() => {
                onFormUpdate('leadReason', values.leadReason)
              }}
              name="leadReason"
              options={selectReasonOptions}
              placeholder={t('contact-form:casavo.reason.placeholder')}
            />
          </div>
          <div className={gridContainer}>
            <div className={inputWrapper}>
              <FormikInput
                handleTracking={() => onFormUpdate('leadName', values.leadName)}
                id="leadName"
                label={t('contact-form:casavo.name')}
                name="leadName"
                type="text"
              />
            </div>
            <div className={inputWrapper}>
              <FormikInput
                required
                handleTracking={() => onFormUpdate('leadEmail', values.leadEmail)}
                id="leadEmail"
                label={t('contact-form:email.label')}
                name="leadEmail"
                type="email"
              />
            </div>
          </div>
          <div className={inputWrapper}>
            <FormikInput
              required
              handleTracking={() => onFormUpdate('leadPhoneNumber', values.leadPhoneNumber)}
              handleTrackingPrefix={() => onFormUpdate('leadPhoneNumberPrefix', values.leadPhoneNumberPrefix)}
              id="leadPhoneNumber"
              inputMode="tel"
              label={t('contact-form:phone')}
              name="leadPhoneNumber"
              prefixName="leadPhoneNumberPrefix"
              type="tel"
            />
          </div>
        </div>

        {showGdpr && (
          <motion.div
            animate={{ height: '100%', opacity: 1 }}
            className={gdprMotionDiv}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.6, type: 'tween' }}
          >
            {isLocaleEnabledFor('consentsAgencyForm', lang) && (
              <FormikCheckbox
                className={sprinkles({ marginBottom: 0, marginTop: 'l' })}
                handleTracking={() =>
                  onFormUpdate('consentForCasavoMortgageService', values.consentForCasavoMortgageService)
                }
                id="consentForCasavoMortgageService"
                label={<Trans components={[<strong key="0" />]} i18nKey={'privacy:mortgage.title'} />}
                name="consentForCasavoMortgageService"
                subText={
                  <ReadMoreToggle
                    fullText={t('privacy:mortgage.full-text')}
                    shortText={
                      <Trans
                        components={[<span key="0" className={sprinkles({ textDecoration: 'underline' })} />]}
                        i18nKey={'privacy:mortgage.short-text'}
                      />
                    }
                  />
                }
              />
            )}
            <FormikCheckbox
              className={sprinkles({
                marginBottom: 0,
                marginTop: 'l',
              })}
              handleTracking={() => onFormUpdate('marketingComunication', values.hasAcceptedMarketing)}
              id="marketing-communications"
              label={<Trans components={[<strong key="0" />]} i18nKey={'privacy:gdpr.marketing-consent-checkbox'} />}
              name="hasAcceptedMarketing"
              subText={
                <ReadMoreToggle
                  fullText={t('privacy:gdpr.marketing-consent-full-text')}
                  shortText={
                    <Trans
                      components={[<span key="0" className={sprinkles({ textDecoration: 'underline' })} />]}
                      i18nKey={'privacy:gdpr.marketing-consent-details'}
                    />
                  }
                />
              }
            />
          </motion.div>
        )}

        {hasError && (
          <motion.div
            animate={{ height: '100%', opacity: 1, y: 0 }}
            initial={{ height: 0, opacity: 0, y: -10 }}
            transition={{ duration: 0.6, type: 'tween' }}
          >
            <Toast className={errorToast} styleType="error" title={`${t('common:form-error')} 😔`} />
          </motion.div>
        )}

        <>
          {device === 'mobile' && !property.detail?.casavoDirectSale && property.country !== 'italy' && (
            <>
              <Divider />
              <CasavoAssistantPhone number={phoneNumber} />
            </>
          )}

          <Disclaimer className={disclaimer} cta={submitLabel} />
          <DisclaimerRecaptcha className={recaptchaDisclaimer} />

          <Button
            center
            className={marginBottom['m-l']}
            disabled={isButtonDisabled}
            label={submitLabel}
            loading={loading}
            minWidth={225}
            type="submit"
          />
        </>
      </form>
    </>
  )
}

export default FormContentCasavo
